import { useIntl } from 'react-intl'
import { useState, useCallback, useContext } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferDescriptionLoadingSkeleton from 'components/Common/Loading/OfferDescriptionLoadingSkeleton'
import AISparkleIcon from 'components/Search/SearchForm/AISparkleIcon'
import OfferDescriptionInteractionContext from './OfferDescriptionInteractionContext'

const DescriptionContainer = styled.div`
  padding: ${rem(8)} ${rem(12)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const InlineSparkleIcon = styled.span`
  display: inline-flex;
  vertical-align: text-bottom;
  margin-inline-end: ${rem(4)};
`

const StyledList = styled.ul`
  margin: 0;
  padding-inline-start: ${rem(12)};
`

const ToggleHint = styled.span`
  margin-inline-start: ${rem(4)};
  color: ${props => props.theme.palette.brand.primary.normal};
  text-decoration: underline;
`

const PlainText = styled.span`
  display: inline;
`

const SPARKLE_ICON = (
  <InlineSparkleIcon>
    <AISparkleIcon size="S" />
  </InlineSparkleIcon>
)

interface Props {
  description?: Array<string>
  isLoading?: boolean
  className?: string
}

function OfferDescription({ description, isLoading = false, className }: Props) {
  const intl = useIntl()
  const [isExpanded, setIsExpanded] = useState(false)
  const onDescriptionExpand = useContext(OfferDescriptionInteractionContext)

  const showLessLabel = intl.formatMessage({
    id: 'zCjrQH89LisG3yZXhGI0',
    defaultMessage: 'Show less',
    description: 'Toggle label to collapse the AI offer description in the offer listing section',
  })
  const showMoreLabel = intl.formatMessage({
    id: 'TyBN8pq5GqCn5ViXprPo',
    defaultMessage: 'Show more',
    description: 'Toggle label to expand the AI offer description in the offer listing section',
  })

  const lines = description ?? []
  const primaryLine = lines[0] ?? ''
  const hasMultipleLines = lines.length > 1

  const toggleExpanded = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    if (!isExpanded) {
      onDescriptionExpand()
    }
    setIsExpanded(previous => !previous)
  }, [isExpanded, onDescriptionExpand])

  if (isLoading) {
    return <OfferDescriptionLoadingSkeleton className={className} />
  }

  if (!description || description.length === 0) {
    return null
  }

  const descriptionContent = !hasMultipleLines ? (
    <PlainText>{primaryLine}</PlainText>
  ) : isExpanded ? (
    <>
      <StyledList>
        {lines.map((line, index) => (
          <li key={`${line}-${index}`}>
            {line}
          </li>
        ))}
      </StyledList>
      <ToggleHint>{showLessLabel}</ToggleHint>
    </>
  ) : (
    <>
      <PlainText>{primaryLine}</PlainText>
      <ToggleHint>{showMoreLabel}</ToggleHint>
    </>
  )

  return (
    <DescriptionContainer className={className}>
      <BodyText
        variant="medium"
        colour="light-foreground-alt"
        onClick={hasMultipleLines ? toggleExpanded : undefined}
      >
        {(!hasMultipleLines || !isExpanded) && SPARKLE_ICON}
        {descriptionContent}
      </BodyText>
    </DescriptionContainer>
  )
}

export default OfferDescription
